import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AnalysisApiClientAccess,
  ApplicationApiClientAccess,
  AppRoleService,
  AppService,
  checkIdentityRole,
} from '@nk/access-block';
import { CachingService } from '@nk/caching-lib';
import {
  AppFeatureKey,
  AppRoleDims,
  AppRoleKey,
  CustomerPersonCompositeBatchEntity,
  CustomerPersonEntity,
  CustomerPersonEntityBase,
  CustomerPersonForm,
  CustomerPersonLocationEntity,
  CustomerPersonLocationEntityBase,
  CustomerPersonNationalIdentificationEntity,
  CustomerPersonNationalIdentificationEntityBase,
  EntityMonitoringReasonEntity,
  EntityMonitoringTypeEntity,
  ScreeningActionCompositeEntity,
  ScreeningActionEntity,
  ScreeningActionPatchPayload,
  ScreeningActionPostPayload,
  ScreeningAdverseMediaEntity,
  ScreeningAuditLogCompositeEntity,
  ScreeningAuditLogReportMetaCompositeEntity,
  ScreeningAuditLogReportMetaPartialEntity,
  ScreeningConfigCompositeChanges,
  ScreeningConfigCompositeEntity,
  ScreeningConfigEntity,
  ScreeningConfigPartialCompositeEntity,
  ScreeningConfigPartialEntity,
  ScreeningConfigScreeningListCategoryEntity,
  ScreeningConfigScreeningListCategoryPartialEntity,
  ScreeningConfigStatsAggregateEntity,
  ScreeningConfigSystemSuggestionSettingEntity,
  ScreeningConfigWatchlistEntity,
  ScreeningConfigWatchlistPartialEntity,
  ScreeningEntityEntity,
  ScreeningEntityPostPayload,
  ScreeningListCategoryEntity,
  ScreeningNationalIdentificationSchemaEntity,
  SimpleCollection,
  WithUuid,
} from '@nk/data-entities-lib';
import { OrderBy } from '@nk/utils-lib';
import * as dayjs from 'dayjs';
import { combineLatest, forkJoin, iif, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  postScreeningConfigCompositeObservable,
  updateScreeningConfigCompositeObservable,
} from '../utils/screening-config-view.utils';
import { DEFAULT_MONITORING_ADD_DECISION_COMMENT } from '../utils/screening.utils';
import { MonitoringOriginEntityService } from './monitoring-origin-entity.service';

interface ScreeningEntityMonitoringBulkDecision {
  app_membership_uuid: string;
  approved_flag: boolean;
  decision_comment: string;
  screening_config_uuid: string;
}

@Injectable()
export class ScreeningConfigService {
  constructor(
    private _analysisApiAccess: AnalysisApiClientAccess,
    private _applicationApiAccess: ApplicationApiClientAccess,
    private _cachingService: CachingService,
    private _appService: AppService,
    private _appRoleService: AppRoleService,
    private _monitoringOriginEntityService: MonitoringOriginEntityService,
  ) {}

  public checkIdentityRole(): Observable<AppRoleDims> {
    return this._appRoleService.activeAppRoleDimsCache.get().pipe(checkIdentityRole([AppRoleKey.aml]));
  }

  public screeningConfigsListCache = this._cachingService.create(this, this.screeningConfigsList);
  public screeningConfigsList(appCustomerUuid: string): Observable<ScreeningConfigEntity[]> {
    return this._analysisApiAccess.get<ScreeningConfigEntity[]>(`/app-customer/${appCustomerUuid}/screening-config`);
  }

  public screeningConfigCompositeCache = this._cachingService.create(this, this.screeningConfigComposite);
  public screeningConfigComposite(screeningConfigUuid: string): Observable<ScreeningConfigCompositeEntity> {
    return this.checkIdentityRole().pipe(
      switchMap(() =>
        this._analysisApiAccess.get<ScreeningConfigCompositeEntity>(
          `/screening-config-composite/${screeningConfigUuid}`,
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        ),
      ),
    );
  }

  public postScreeningConfig(payload: ScreeningConfigPartialEntity): Observable<ScreeningConfigEntity> {
    return combineLatest([this.checkIdentityRole(), this._appService.identityDataCache.get()]).pipe(
      switchMap(([roleDims, identity]) =>
        this._analysisApiAccess.post<ScreeningConfigEntity>(
          `/app-customer/${identity.app_customer.uuid}/screening-config`,
          payload,
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        ),
      ),
    );
  }

  public postScreeningConfigComposite(
    payload: ScreeningConfigPartialCompositeEntity,
  ): Observable<ScreeningConfigCompositeEntity> {
    return postScreeningConfigCompositeObservable(
      payload,
      this.postScreeningConfig.bind(this),
      this.postScreeningConfigWatchlist.bind(this),
      this.postScreeningConfigScreeningListCategory.bind(this),
    );
  }

  public patchScreeningConfig(
    screeningConfigUuid: string,
    payload: ScreeningConfigPartialEntity,
  ): Observable<ScreeningConfigEntity> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        this.screeningConfigsListCache.invalidateAllCache();
        this.screeningConfigCompositeCache.invalidateAllCache();
        return this._analysisApiAccess.patch<ScreeningConfigEntity>(
          `/screening-config/${screeningConfigUuid}`,
          payload,
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public postScreeningConfigWatchlist(
    screeningConfigUuid: string,
    payload: ScreeningConfigWatchlistPartialEntity,
  ): Observable<ScreeningConfigWatchlistEntity> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.post<ScreeningConfigWatchlistEntity>(
          `/screening-config/${screeningConfigUuid}/screening-config-watchlist`,
          payload,
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public deleteScreeningConfigWatchlist(
    screeningConfigUuid: string,
    screeningConfigWatchlistUuid: string,
  ): Observable<ScreeningConfigWatchlistEntity> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.delete<ScreeningConfigWatchlistEntity>(
          `/screening-config/${screeningConfigUuid}/screening-config-watchlist/${screeningConfigWatchlistUuid}`,
          undefined,
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public postScreeningConfigScreeningListCategory(
    screeningConfigUuid: string,
    payload: ScreeningConfigScreeningListCategoryPartialEntity,
  ): Observable<ScreeningConfigScreeningListCategoryEntity> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.post<ScreeningConfigScreeningListCategoryEntity>(
          `/screening-config/${screeningConfigUuid}/screening-config-screening-list-category`,
          payload,
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public deleteScreeningConfigScreeningListCategory(
    screeningConfigUuid: string,
    screeningConfigScreeningListCategoryUuid: string,
  ): Observable<ScreeningConfigScreeningListCategoryEntity> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.delete<ScreeningConfigScreeningListCategoryEntity>(
          `/screening-config/${screeningConfigUuid}/screening-config-screening-list-category/${screeningConfigScreeningListCategoryUuid}`,
          undefined,
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public updateScreeningConfigComposite(
    screeningConfigCompositeChanges: ScreeningConfigCompositeChanges,
  ): Observable<true> {
    return updateScreeningConfigCompositeObservable(
      screeningConfigCompositeChanges,
      this.patchScreeningConfig.bind(this),
      this.postScreeningConfigWatchlist.bind(this),
      this.deleteScreeningConfigWatchlist.bind(this),
      this.postScreeningConfigScreeningListCategory.bind(this),
      this.deleteScreeningConfigScreeningListCategory.bind(this),
    );
  }

  public postJobScreeningAction(screeningConfigUuid: string, companyUuid: string | null = null): Observable<unknown> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('async_flag', false);
    const payload = {
      screening_config_uuid: screeningConfigUuid,
      related_company_uuid: companyUuid,
    };
    return this._applicationApiAccess.post<unknown>(
      `/task/screening-suggest-entities-for-monitoring/job`,
      payload,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.screening],
      [AppRoleKey.aml],
    );
  }

  public screeningConfigStatsAggregateCache = this._cachingService.create(
    this,
    this.fetchScreeningConfigStatsAggregate,
  );

  public fetchScreeningConfigStatsAggregate(
    screeningConfigUuid: string,
  ): Observable<ScreeningConfigStatsAggregateEntity | null> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess
          .get<
            ScreeningConfigStatsAggregateEntity[]
          >(`/screening-config/${screeningConfigUuid}/screening-config-stats-aggregate`, {}, [AppFeatureKey.base, AppFeatureKey.screening], [AppRoleKey.aml])
          .pipe(map((data) => (data && data.length ? data[0] : null)));
      }),
    );
  }

  public screeningConfigWatchlistListCache = this._cachingService.create(this, this.screeningConfigWatchlistList);
  public screeningConfigWatchlistList(
    companyUuid: string,
    screeningConfigUuid: string,
    watchlistUuid: string | null = null,
  ): Observable<ScreeningConfigWatchlistEntity[]> {
    let params = new HttpParams();
    if (companyUuid) {
      params = params.append('company_uuid', companyUuid);
    }
    if (watchlistUuid) {
      params = params.append('watchlist_uuid', watchlistUuid);
    }
    return this._analysisApiAccess.get<ScreeningConfigWatchlistEntity[]>(
      `/screening-config/${screeningConfigUuid}/screening-config-watchlist`,
      { params },
    );
  }

  public entityMonitoringReasonCache = this._cachingService.create(this, this.fetchEntityMonitoringReasons);

  public fetchEntityMonitoringReasons(): Observable<EntityMonitoringReasonEntity[]> {
    return this._analysisApiAccess.get<EntityMonitoringReasonEntity[]>(
      `/entity-monitoring-reason`,
      {},
      [AppFeatureKey.base, AppFeatureKey.screening],
      [AppRoleKey.aml],
    );
  }

  public screeningActionCompositeCollectionCache = this._cachingService.create(
    this,
    this.fetchScreeningActionCompositeCollection,
  );
  public fetchScreeningActionCompositeCollection(
    screeningConfigUuid: string,
    monitoringOriginEntityUuid: string | undefined,
    screeningEntityUuid: string | undefined,
    approvedFlag: boolean | undefined,
    orderBy: OrderBy[],
    limit: number,
    offset: number,
    onlyPendingFlag = false,
  ): Observable<SimpleCollection<ScreeningActionCompositeEntity>> {
    let params = new HttpParams();
    if (onlyPendingFlag) {
      params = params.append('only_pending_flag', true);
    }
    if (typeof approvedFlag === 'boolean') {
      params = params.append('approved_flag', approvedFlag);
    }
    if (monitoringOriginEntityUuid) {
      params = params.append('monitoring_origin_entity_uuid', monitoringOriginEntityUuid);
    }
    if (screeningEntityUuid) {
      params = params.append('screening_entity_uuid', screeningEntityUuid);
    }
    if (orderBy) {
      params = params.append('order_by', orderBy.join(','));
    }
    params = params.append('limit', limit);
    params = params.append('offset', offset);
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.get<SimpleCollection<ScreeningActionCompositeEntity>>(
          `/screening-config/${screeningConfigUuid}/screening-action-composite-collection`,
          { params },
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  private _postCustomerPersonEntity(
    screeningConfigUuid: string,
    payload: CustomerPersonEntityBase,
  ): Observable<WithUuid<CustomerPersonEntity>> {
    // Send as YYYY-MM-DD — calendar date, not a UTC timestamp.
    const body = {
      ...payload,
      birth_date: payload.birth_date ? dayjs(payload.birth_date).format('YYYY-MM-DD') : null,
      screening_config_uuid: screeningConfigUuid,
    };
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.post<WithUuid<CustomerPersonEntity>>(
          `/screening-config/${screeningConfigUuid}/customer-person`,
          body,
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  private _postCustomerPersonNationalIdentificationEntity(
    customerPersonUuid: string,
    payload: CustomerPersonNationalIdentificationEntityBase,
  ): Observable<CustomerPersonNationalIdentificationEntity> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.post<CustomerPersonNationalIdentificationEntity>(
          `/customer-person/${customerPersonUuid}/customer-person-national-identification`,
          { ...payload, customer_person_uuid: customerPersonUuid },
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  private _postCustomerPersonLocationEntity(
    customerPersonUuid: string,
    payload: CustomerPersonLocationEntityBase,
  ): Observable<CustomerPersonLocationEntity> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.post<CustomerPersonLocationEntity>(
          `/customer-person/${customerPersonUuid}/customer-person-location`,
          { ...payload, customer_person_uuid: customerPersonUuid },
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  /**
   * Creates custom persons (connected to a company) and private persons (those without connection)
   */
  public createCustomerPerson(
    screeningConfigUuid: string,
    monitoringOriginEntityUuid: string | undefined,
    appMembershipUuid: string,
    entityMonitoringTypeUuid: string,
    monitoringComment: string,
    customerPersonForm: CustomerPersonForm,
  ): Observable<CustomerPersonEntity> {
    return this._postCustomerPersonEntity(screeningConfigUuid, customerPersonForm.customer_person).pipe(
      switchMap((customerPerson) =>
        this.postScreeningEntityEntity({
          entity_uuid: null,
          entity_key: 'customer_person',
          customer_person_uuid: customerPerson.uuid,
        }).pipe(
          switchMap((screeningEntity) => {
            let monitoringOriginEntityUuid$: Observable<string>;
            if (monitoringOriginEntityUuid) {
              // customer person is connected to a company
              monitoringOriginEntityUuid$ = of(monitoringOriginEntityUuid);
            } else {
              // customer person is not connected to a company (private customer person)
              monitoringOriginEntityUuid$ = this._monitoringOriginEntityService
                .postMonitoringOriginEntityEntity(screeningConfigUuid, {
                  screening_config_uuid: screeningConfigUuid,
                  screening_entity_uuid: screeningEntity.uuid,
                  monitored_flag: true,
                })
                .pipe(map((monitoringOriginEntity) => monitoringOriginEntity.uuid));
            }
            return monitoringOriginEntityUuid$.pipe(
              switchMap((monitoringOriginEntityUuid) => {
                const payload: ScreeningActionPostPayload = {
                  entity_monitoring_type_uuid: entityMonitoringTypeUuid,
                  monitoring_comment: monitoringComment,
                  removed_flag: false,
                  app_membership_uuid: appMembershipUuid,
                  approved_flag: true,
                  decision_comment: DEFAULT_MONITORING_ADD_DECISION_COMMENT,
                  monitoring_origin_entity_uuid: monitoringOriginEntityUuid,
                  screening_entity_uuid: screeningEntity.uuid,
                };
                return this.postScreeningActionEntity(screeningConfigUuid, payload).pipe(
                  switchMap(() =>
                    forkJoin([
                      this._postCustomerPersonNationalIdentificationEntity(
                        customerPerson.uuid,
                        customerPersonForm.national_identification,
                      ),
                      this._postCustomerPersonLocationEntity(
                        customerPerson.uuid,
                        customerPersonForm.residential_address,
                      ),
                    ]).pipe(map(() => customerPerson)),
                  ),
                );
              }),
            );
          }),
        ),
      ),
    );
  }

  public postCustomerPersonCompositeBatch(
    screeningConfigUuid: string,
    payload: CustomerPersonCompositeBatchEntity[],
  ): Observable<CustomerPersonCompositeBatchEntity> {
    // Send birth_date as YYYY-MM-DD — calendar date, not a UTC timestamp.
    const body = payload.map((composite) => ({
      ...composite,
      customer_person_batch: {
        ...composite.customer_person_batch,
        birth_date: composite.customer_person_batch.birth_date
          ? dayjs(composite.customer_person_batch.birth_date).format('YYYY-MM-DD')
          : null,
      },
    }));
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.post<CustomerPersonCompositeBatchEntity>(
          `/screening-config/${screeningConfigUuid}/customer-person-composite-batch`,
          body,
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
      tap(() => {
        this.screeningActionCompositeCollectionCache.invalidateAllCache();
      }),
    );
  }

  public screeningEntityCache = this._cachingService.create(this, this.fetchScreeningEntity);
  public fetchScreeningEntity(screeningEntityUuid: string): Observable<WithUuid<ScreeningEntityEntity>> {
    return this._analysisApiAccess.get<WithUuid<ScreeningEntityEntity>>(
      `/screening-entity/${screeningEntityUuid}`,
      {},
      [AppFeatureKey.base, AppFeatureKey.screening],
      [AppRoleKey.aml],
    );
  }

  public screeningEntityListCache = this._cachingService.create(this, this.fetchScreeningEntityList);
  public fetchScreeningEntityList(
    entityUuid: string | null = null,
    customerPersonUuid: string | null = null,
  ): Observable<WithUuid<ScreeningEntityEntity>[]> {
    let httpParams = new HttpParams();
    if (entityUuid) {
      httpParams = httpParams.append('entity_uuid', entityUuid);
    }
    if (customerPersonUuid) {
      httpParams = httpParams.append('customer_person_uuid', customerPersonUuid);
    }
    return this._analysisApiAccess.get<WithUuid<ScreeningEntityEntity>[]>(
      `/screening-entity`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.screening],
      [AppRoleKey.aml],
    );
  }

  public screeningAdverseMediaCollectionCache = this._cachingService.create(
    this,
    this.fetchScreeningAdverseMediaCollection,
  );
  public fetchScreeningAdverseMediaCollection(
    screeningEntityUuid: string,
    limit = 100,
    offset = 0,
  ): Observable<SimpleCollection<ScreeningAdverseMediaEntity>> {
    let params = new HttpParams();
    params = params.append('limit', limit);
    params = params.append('offset', offset);
    return this._analysisApiAccess.get<SimpleCollection<ScreeningAdverseMediaEntity>>(
      `/screening-entity/${screeningEntityUuid}/screening-adverse-media-collection`,
      { params },
      [AppFeatureKey.base, AppFeatureKey.screening],
      [AppRoleKey.aml],
    );
  }

  public postScreeningEntityEntity(payload: ScreeningEntityPostPayload): Observable<WithUuid<ScreeningEntityEntity>> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.post<WithUuid<ScreeningEntityEntity>>(
          `/screening-entity`,
          payload,
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public postScreeningActionEntity(
    screeningConfigUuid: string,
    payload: ScreeningActionPostPayload,
  ): Observable<ScreeningActionEntity> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.post<ScreeningActionEntity>(
          `/screening-config/${screeningConfigUuid}/screening-action`,
          payload,
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public patchScreeningActionEntity(
    screeningConfigUuid: string,
    payload: ScreeningActionPatchPayload,
  ): Observable<ScreeningActionEntity> {
    return this.checkIdentityRole().pipe(
      switchMap(() => {
        return this._analysisApiAccess.patch<ScreeningActionEntity>(
          `/screening-config/${screeningConfigUuid}/screening-action/${payload.uuid}`,
          payload,
          {},
          [AppFeatureKey.base, AppFeatureKey.screening],
          [AppRoleKey.aml],
        );
      }),
    );
  }

  public patchScreeningActionEntities(
    screeningConfigUuid: string,
    payload: ScreeningActionPatchPayload[],
  ): Observable<ScreeningActionEntity[]> {
    return this._appService.identityDataCache.get().pipe(
      switchMap(() =>
        iif(
          () => !!(payload && payload.length),
          forkJoin(
            payload.map((suggestion) =>
              this.patchScreeningActionEntity(screeningConfigUuid, {
                ...suggestion,
              }),
            ),
          ),
          of<ScreeningActionEntity[]>([]),
        ),
      ),
    );
  }

  public entityMonitoringTypeListCache = this._cachingService.create(this, this.fetchEntityMonitoringTypeList);
  public fetchEntityMonitoringTypeList(): Observable<EntityMonitoringTypeEntity[]> {
    return this._analysisApiAccess.get<EntityMonitoringTypeEntity[]>(`/entity-monitoring-type`, {}, [
      AppFeatureKey.base,
      AppFeatureKey.screening,
    ]);
  }

  public screeningNationalIdentificationSchemaListCache = this._cachingService.create(
    this,
    this.screeningNationalIdentificationSchemaList,
  );
  public screeningNationalIdentificationSchemaList(): Observable<ScreeningNationalIdentificationSchemaEntity[]> {
    return this._analysisApiAccess.get<ScreeningNationalIdentificationSchemaEntity[]>(
      `/screening-national-identification-schema`,
      {},
      [AppFeatureKey.base, AppFeatureKey.screening],
    );
  }

  public screeningListCategoryListCache = this._cachingService.create(this, this.screeningListCategoryList);
  public screeningListCategoryList(): Observable<ScreeningListCategoryEntity[]> {
    return this._analysisApiAccess.get<ScreeningListCategoryEntity[]>(
      `/screening-list-category`,
      {},
      [AppFeatureKey.base, AppFeatureKey.screening],
      [AppRoleKey.aml],
    );
  }

  public screeningConfigSystemSuggestionSettingListCache = this._cachingService.create(
    this,
    this.fetchScreeningConfigSystemSuggestionSettingList,
  );
  public fetchScreeningConfigSystemSuggestionSettingList(): Observable<
    WithUuid<ScreeningConfigSystemSuggestionSettingEntity>[]
  > {
    return this._analysisApiAccess.get<WithUuid<ScreeningConfigSystemSuggestionSettingEntity>[]>(
      `/screening-config-system-suggestion-setting`,
      {},
      [AppFeatureKey.base, AppFeatureKey.screening],
      [AppRoleKey.aml],
    );
  }

  public screeningAuditLogCompositeCollectionCache = this._cachingService.create(
    this,
    this.fetchScreeningAuditLogCompositeCollection,
    true,
    3000,
  );
  public fetchScreeningAuditLogCompositeCollection(
    screeningConfigUuid: string,
    monitoringOriginEntityUuid?: string,
    screeningEntityUuid?: string,
    limit = 30,
    offset = 0,
  ): Observable<SimpleCollection<ScreeningAuditLogCompositeEntity>> {
    let params = new HttpParams();

    if (monitoringOriginEntityUuid) {
      params = params.append('monitoring_origin_entity_uuid', monitoringOriginEntityUuid);
    }
    if (screeningEntityUuid) {
      params = params.append('screening_entity_uuid', screeningEntityUuid);
    }
    if (typeof limit === 'number') {
      params = params.append('limit', String(limit));
    }
    if (typeof offset === 'number') {
      params = params.append('offset', String(offset));
    }

    return this._analysisApiAccess.get<SimpleCollection<ScreeningAuditLogCompositeEntity>>(
      `/screening-config/${screeningConfigUuid}/screening-audit-log-composite-collection`,
      { params },
      [AppFeatureKey.base, AppFeatureKey.screening],
      [AppRoleKey.aml],
    );
  }

  public screeningAuditLogReportMetaCompositeListCache = this._cachingService.create(
    this,
    this.fetchScreeningAuditLogReportMetaCompositeList,
  );
  public fetchScreeningAuditLogReportMetaCompositeList(
    screeningConfigUuid: string,
    companyUuid: string,
  ): Observable<ScreeningAuditLogReportMetaCompositeEntity[]> {
    let httpParams = new HttpParams();

    if (companyUuid) {
      httpParams = httpParams.append('company_uuid', companyUuid);
    }

    return this._analysisApiAccess.get<ScreeningAuditLogReportMetaCompositeEntity[]>(
      `/screening-config/${screeningConfigUuid}/screening-audit-log-report-meta-composite`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.screening],
      [AppRoleKey.aml],
    );
  }

  public postScreeningAuditLogReportMeta(
    screeningConfigUuid: string,
    payload: ScreeningAuditLogReportMetaPartialEntity,
    fileData: File,
  ): Observable<ScreeningAuditLogReportMetaCompositeEntity> {
    const formData: FormData = new FormData();
    formData.append('file_data', fileData, fileData.name);
    for (const key of Object.keys(payload)) {
      formData.append(key, payload[key as keyof typeof payload]);
    }

    return this._applicationApiAccess.post<ScreeningAuditLogReportMetaCompositeEntity>(
      `/screening-config/${screeningConfigUuid}/screening-audit-log-report-meta-composite`,
      formData,
      {},
      [AppFeatureKey.base, AppFeatureKey.screening],
      [AppRoleKey.aml],
    );
  }
}
